import CloseButton from '@atoms/CloseButton/CloseButton';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import QuantitySelector from '@atoms/QuantitySelector/QuantitySelector';
import { IBannerNotification } from '@models/IBannerNotification';
import { ICartItem } from '@models/ICartItem';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import BasketNotifications from '@molecules/BasketNotifications/BasketNotifications';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';

interface Props extends ICartItem {
  readonly amountSaveLabel: string;
  readonly desktopBreakpoint?: BreakpointEnum.XLarge | BreakpointEnum.Medium;
  readonly index?: number;
  readonly isInTray?: boolean;
  readonly onChangeCartItemQuantity: (code: string, quantity: number) => void;
  readonly onRemoveItemFromCart: (code: string, isGift?: boolean) => void;
  readonly showLessLabel?: string;
  readonly showMoreLabel?: string;
}

const CartItem = ({
  amountLabels,
  amountSaveLabel,
  amountSaved,
  availableQuantities,
  backOrderNotification,
  code,
  desktopBreakpoint = BreakpointEnum.XLarge,
  discountedPrice,
  displayName,
  errorNotification,
  giftNotification,
  index,
  isFreeItem,
  isInTray,
  lineItemTag,
  onChangeCartItemQuantity,
  onRemoveItemFromCart,
  placedPrice,
  preOrderNotification,
  quantityLabel,
  selectedQuantity,
  showLessLabel,
  showMoreLabel,
  stockStatus,
  stockStatusLabel,
  thumbnail,
  url,
}: Props) => {
  const isMobile = useMediaQuery(1440);
  const isDiscounted = placedPrice !== discountedPrice;
  const itemNotifications = [
    preOrderNotification,
    backOrderNotification,
    errorNotification,
    giftNotification,
  ].filter((notification) => notification) as IBannerNotification[];

  return (
    <motion.div
      key={code}
      data-testid={`Cart-Item${index ? `-${index}` : ''}`}
      className={classNames(
        'relative rounded-[10px] border border-grey bg-white',
        isInTray ? 'p-4' : 'p-2.5',
        {
          'xl:flex xl:min-h-[14.5625rem] xl:shrink-0 xl:flex-col':
            desktopBreakpoint === BreakpointEnum.XLarge,
          'md:flex md:min-h-[14.5625rem] md:shrink-0 md:flex-col':
            desktopBreakpoint === BreakpointEnum.Medium,
        }
      )}
      layout
      transition={{
        type: 'spring',
        duration: 0.3,
        layout: { type: 'spring', duration: 0.7 },
      }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.8, opacity: 0 }}
    >
      {/* TODO: Revert this conditional ONLY when gift items are able to
          be removed from the basket which currently Opti does not allow */}
      {!isFreeItem && (
        <CloseButton
          className={classNames('absolute top-2.5 right-2.5', {
            'xl:top-[0.9375rem] xl:right-[0.9375rem]':
              desktopBreakpoint === BreakpointEnum.XLarge,
            'md:top-[0.9375rem] md:right-[0.9375rem]':
              desktopBreakpoint === BreakpointEnum.Medium,
          })}
          size="1.375rem"
          onClick={() => {
            onRemoveItemFromCart(code, isFreeItem);
          }}
        />
      )}

      {lineItemTag && (
        <div
          className={classNames('mb-4 flex', {
            'xl:hidden': desktopBreakpoint === BreakpointEnum.XLarge,
            'md:hidden': desktopBreakpoint === BreakpointEnum.Medium,
          })}
          data-testid={`Cart-Item${index ? `-${index}` : ''}-Tag-Mobile`}
        >
          <div className="flex items-center gap-x-1.5 rounded-[100px] bg-grey-100 px-3 py-1">
            <Icon
              name={lineItemTag.icon}
              colorClassName="text-yellow"
              size={IconSizeEnum.md}
            />

            <p className="body-four">{lineItemTag.label}</p>
          </div>
        </div>
      )}

      <div
        className={classNames('flex h-full grow gap-x-5', {
          'xl:w-full xl:gap-x-7.5': desktopBreakpoint === BreakpointEnum.XLarge,
          'md:w-full md:gap-x-7.5': desktopBreakpoint === BreakpointEnum.Medium,
        })}
      >
        {isInTray ? (
          <div className="flex flex-col gap-y-5">
            <a
              data-testid={`Cart-Item${index ? `-${index}` : ''}-Image-Link`}
              href={url}
              target="_blank"
              rel="noreferrer"
              className={classNames('shrink-0 rounded-[10px] bg-grey-100', {
                'max-xl:h-[110px] max-xl:w-[110px] xl:w-[250px]':
                  desktopBreakpoint === BreakpointEnum.XLarge,
                'max-md:h-[110px] max-md:w-[110px] md:w-[250px]':
                  desktopBreakpoint === BreakpointEnum.Medium,
              })}
            >
              <Image
                dataTestId={`Cart-Item${index ? `-${index}` : ''}-Image`}
                image={thumbnail}
                imageClasses="object-contain h-full rounded-[10px] max-h-[250px]"
              />
            </a>

            <div className="md:hidden">
              <QuantitySelector
                code={code}
                onChangeCartItemQuantity={onChangeCartItemQuantity}
                onRemoveItemFromCart={onRemoveItemFromCart}
                quantity={selectedQuantity}
                dataTestId={`Cart-Item${
                  index ? `-${index}` : ''
                }-Quantity-Selector-Mobile`}
              />
            </div>
          </div>
        ) : (
          <>
            <a
              data-testid={`Cart-Item${index ? `-${index}` : ''}-Image-Link`}
              href={url}
              target="_blank"
              rel="noreferrer"
              className={classNames('shrink-0 rounded-[10px] bg-grey-100', {
                'max-xl:h-[110px] max-xl:w-[110px] xl:w-[250px]':
                  desktopBreakpoint === BreakpointEnum.XLarge,
                'max-md:h-[110px] max-md:w-[110px] md:w-[250px]':
                  desktopBreakpoint === BreakpointEnum.Medium,
              })}
            >
              <Image
                dataTestId={`Cart-Item${index ? `-${index}` : ''}-Image`}
                image={thumbnail}
                imageClasses="object-contain h-full rounded-[10px] max-h-[250px]"
              />
            </a>
          </>
        )}

        <div
          className={classNames(
            'flex w-full min-w-0 flex-col gap-y-5 py-2.5 pr-2.5',
            {
              'xl:justify-between xl:gap-y-4 xl:py-5':
                desktopBreakpoint === BreakpointEnum.XLarge,
              'md:justify-between md:gap-y-4 md:py-5':
                desktopBreakpoint === BreakpointEnum.Medium,
            }
          )}
        >
          <div>
            {lineItemTag && (
              <div
                className={classNames('hidden', {
                  'xl:mb-[0.9375rem] xl:flex':
                    desktopBreakpoint === BreakpointEnum.XLarge,
                  'md:mb-[0.9375rem] md:flex':
                    desktopBreakpoint === BreakpointEnum.Medium,
                })}
                data-testid={`Cart-Item${index ? `-${index}` : ''}-Tag-Desktop`}
              >
                <div className="flex items-center gap-x-[0.4375rem] rounded-[100px] bg-grey-100 py-1 pr-4 pl-[0.4375rem]">
                  <Icon name={lineItemTag.icon} colorClassName="text-yellow" />

                  <p className="body-two">{lineItemTag.label}</p>
                </div>
              </div>
            )}

            <p
              data-testid={`Cart-Item${index ? `-${index}` : ''}-Stock-Status`}
              className={classNames(
                'mb-[0.3125rem] text-grey-600',
                isMobile ? 'body-four' : 'body-three body-three--bold',
                {
                  'text-red-100':
                    stockStatus === ProductStockStatusEnum.OutOfStock,
                  'xl:mb-1.5': desktopBreakpoint === BreakpointEnum.XLarge,
                  'md:mb-1.5': desktopBreakpoint === BreakpointEnum.Medium,
                }
              )}
            >
              {stockStatusLabel}
            </p>

            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              data-testid={`Cart-Item${index ? `-${index}` : ''}-Name`}
              className={classNames('inline-block w-full', {
                'xl:w-[45%]': desktopBreakpoint === BreakpointEnum.XLarge,
                'md:w-[45%]': desktopBreakpoint === BreakpointEnum.Medium,
              })}
            >
              <p
                className={classNames(
                  'line-clamp-3 w-full break-words',
                  isMobile ? 'body-one' : 'h6',
                  {
                    'xl:line-clamp-2':
                      desktopBreakpoint === BreakpointEnum.XLarge,
                    'md:line-clamp-2':
                      desktopBreakpoint === BreakpointEnum.Medium,
                  }
                )}
              >
                {displayName}
              </p>
            </a>
          </div>

          <div
            className={classNames('flex flex-col-reverse gap-5', {
              'xl:flex-row xl:items-end xl:justify-between':
                desktopBreakpoint === BreakpointEnum.XLarge,
              'md:flex-row md:items-end md:justify-between':
                desktopBreakpoint === BreakpointEnum.Medium,
            })}
          >
            {isInTray ? (
              <div className="max-md:hidden">
                <QuantitySelector
                  code={code}
                  onChangeCartItemQuantity={onChangeCartItemQuantity}
                  onRemoveItemFromCart={onRemoveItemFromCart}
                  quantity={selectedQuantity}
                  dataTestId={`Cart-Item${
                    index ? `-${index}` : ''
                  }-Quantity-Selector-Desktop`}
                />
              </div>
            ) : (
              <div
                className={classNames('flex items-center gap-x-2.5', {
                  'xl:gap-x-5': desktopBreakpoint === BreakpointEnum.XLarge,
                  'md:gap-x-5': desktopBreakpoint === BreakpointEnum.Medium,
                })}
                data-testid={`Cart-Item${
                  index ? `-${index}` : ''
                }-Quantity-Selector`}
              >
                <p className={isMobile ? 'body-two' : 'h6'}>{quantityLabel}</p>

                {!isFreeItem ? (
                  <div className="relative">
                    <select
                      aria-label="Quantity selector"
                      disabled={
                        stockStatus === ProductStockStatusEnum.OutOfStock
                      }
                      value={selectedQuantity}
                      onChange={(ev) => {
                        const newSelectedQuantity = Number(ev.target.value);
                        const updatedQuantity =
                          newSelectedQuantity - selectedQuantity;

                        onChangeCartItemQuantity?.(code, updatedQuantity);
                      }}
                      className="body-three relative appearance-none rounded-[100px] bg-grey-100 py-1.5 pr-10 pl-5"
                    >
                      {Object.entries(availableQuantities).map(
                        ([entryLabel, entryValue]) => (
                          <option key={entryValue} value={entryValue}>
                            {entryLabel}
                          </option>
                        )
                      )}
                    </select>

                    <div className="pointer-events-none absolute top-0 right-[0.8125rem] z-10 flex h-full items-center">
                      <Icon
                        name="caret_down"
                        {...(stockStatus === ProductStockStatusEnum.OutOfStock
                          ? { colorClassName: 'text-grey-line' }
                          : {})}
                      />
                    </div>
                  </div>
                ) : (
                  <span className={classNames(isMobile ? 'body-two' : 'h6')}>
                    {selectedQuantity}
                  </span>
                )}
              </div>
            )}

            <div
              className={classNames('flex flex-col gap-y-[0.3125rem]', {
                'xl:items-end xl:gap-y-px':
                  desktopBreakpoint === BreakpointEnum.XLarge,
                'md:items-end md:gap-y-px':
                  desktopBreakpoint === BreakpointEnum.Medium,
                'md:ml-auto': isFreeItem,
              })}
            >
              <div className="flex items-end gap-x-2.5">
                {isDiscounted && (
                  <p
                    data-testid={`Cart-Item${
                      index ? `-${index}` : ''
                    }-Full-Price`}
                    className={classNames(
                      'line-through',
                      isMobile
                        ? 'body-one text-grey-450'
                        : 'font-light text-base leading-24'
                    )}
                  >
                    {placedPrice}
                  </p>
                )}

                <p
                  data-testid={`Cart-Item${
                    index ? `-${index}` : ''
                  }-Final-Price`}
                  className={classNames(
                    isMobile ? 'body-one body-one--bold' : 'h6'
                  )}
                >
                  {discountedPrice}
                </p>
              </div>

              {isDiscounted && (
                <div
                  className="flex items-center gap-x-[0.3125rem]"
                  data-testid={`Cart-Item${
                    index ? `-${index}` : ''
                  }-Saved-Amount`}
                >
                  <div className="h-1.5 w-1.5 rounded-full bg-yellow" />

                  <p className="body-three body-three--bold">
                    {amountSaveLabel.replace('{0}', amountSaved)}
                  </p>
                </div>
              )}

              {amountLabels
                ?.filter((amountLabel) => amountLabel)
                .map((amountLabel) => (
                  <p
                    key={amountLabel}
                    className="body-four text-grey-450"
                    data-testid="Cart-Item-Amount-Label"
                  >
                    {amountLabel}
                  </p>
                ))}
            </div>
          </div>
        </div>
      </div>

      {itemNotifications.length > 0 && (
        <div
          className={classNames('mt-7.5', {
            'xl:mt-5': desktopBreakpoint === BreakpointEnum.XLarge,
            'md:mt-5': desktopBreakpoint === BreakpointEnum.Medium,
          })}
        >
          <BasketNotifications
            notifications={itemNotifications}
            desktopBreakpoint={BreakpointEnum.XLarge}
            isContentExpandable
            showMoreLabel={showMoreLabel}
            showLessLabel={showLessLabel}
            dataTestId={`Cart-Item${index ? `-${index}` : ''}-Notifications`}
          />
        </div>
      )}
    </motion.div>
  );
};

export default CartItem;
